import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { LocalizationService } from '../../core/localization/localization.service';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { GrowthGuidedJourneyDetail, GrowthJourneyService } from './growth-journey.service';
import { GrowthJourneyImprovementPanelComponent } from './growth-journey-improvement-panel.component';
import { GrowthJourneyMonitoringPanelComponent } from './growth-journey-monitoring-panel.component';
import { WorkflowApiService } from '../workflow/workflow-api.service';
import {
  GROWTH_JOURNEY_STEP_ORDER,
  GUIDED_JOURNEY_STATUS_I18N_KEYS,
  growthJourneyLineageKindI18nKey,
  growthJourneyStepI18nKey,
  growthJourneyStepStateI18nKey,
  growthJourneyStepStateSeverity as resolveStepStateSeverity,
  guidedJourneyStatusSeverity as resolveGuidedJourneyStatusSeverity,
  isJourneyMonitoringAvailable,
  normalizeGrowthJourneyStep,
  normalizeGuidedJourneyStatus
} from './growth-status.util';

interface StepperItem {
  step: string;
  state: string;
  isGated: boolean;
  isCurrent: boolean;
}

@Component({
  standalone: true,
  selector: 'ain-growth-journey-dashboard',
  providers: [MessageService],
  imports: [
    AinPageComponent,
    AinStateComponent,
    InfoHintComponent,
    GrowthJourneyMonitoringPanelComponent,
    GrowthJourneyImprovementPanelComponent,
    ButtonModule,
    TagModule,
    RouterLink
  ],
  template: `
    <ain-page
      [eyebrow]="t('growth.eyebrow')"
      [title]="pageTitle()"
      [description]="t('growth.journey.guided.dashboard.description')">
      <div class="toolbar">
        <a pButton routerLink="/growth/journey/journeys" severity="secondary" [label]="t('common.back')"></a>
        @if (journey(); as item) {
          <button
            pButton
            type="button"
            severity="secondary"
            [label]="t('growth.journey.guided.dashboard.refreshState')"
            [loading]="refreshing()"
            (click)="refresh()"></button>
        }
      </div>

      <ain-info-hint
        [titleKey]="'growth.journey.guided.help.title'"
        [descriptionKey]="'growth.journey.guided.help.description'"
        [bulletKeys]="['growth.journey.guided.help.bullets.progress']" />

      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else if (error()) {
        <ain-state type="error" [title]="t('common.error')" [message]="error() ?? ''" />
      } @else if (journey(); as item) {
        <section class="card meta-card">
          <div class="meta-row">
            <p-tag [value]="statusLabel(item.status)" [severity]="statusSeverity(item.status)" />
            <p-tag [value]="t(stepLabelKey(item.currentStep))" severity="contrast" />
          </div>
        </section>

        <section class="card">
          <h3>{{ t('growth.journey.guided.dashboard.progress') }}</h3>
          <ol class="stepper">
            @for (step of stepperItems(); track step.step) {
              <li
                class="stepper-item"
                [class.stepper-item--current]="step.isCurrent"
                [class.stepper-item--gated]="step.isGated">
                <div class="stepper-index">{{ $index + 1 }}</div>
                <div class="stepper-body">
                  <span class="stepper-label">{{ t(stepLabelKey(step.step)) }}</span>
                  <p-tag
                    [value]="t(stepStateLabelKey(step.state))"
                    [severity]="stepStateSeverity(step.state)" />
                </div>
              </li>
            }
          </ol>
        </section>

        <section class="card next-action-card">
          <h3>{{ t('growth.journey.guided.dashboard.nextAction') }}</h3>
          <p class="next-action-hint">{{ t('growth.journey.guided.dashboard.workflowDriven') }}</p>
          @if (workflowLookupDone()) {
            @if (workflowTaskId(); as taskId) {
              <a
                pButton
                [routerLink]="['/workflow/tasks', taskId]"
                [label]="t('growth.journey.guided.dashboard.continueInWorkflow')"></a>
            } @else if (item.workflowProcessInstanceId) {
              <p class="next-action-muted">{{ t('growth.journey.guided.dashboard.noOpenTask') }}</p>
            } @else {
              <p class="next-action-muted">{{ t('growth.journey.guided.dashboard.workflowNotStarted') }}</p>
            }
          }
        </section>

        <section class="card">
          <h3>{{ t('growth.journey.guided.dashboard.lineage') }}</h3>
          <ul class="lineage-list">
            @for (node of item.lineage.nodes; track node.kind) {
              <li class="lineage-item">
                <span class="lineage-kind">{{ t(lineageKindKey(node.kind)) }}</span>
                @if (node.status) {
                  <p-tag [value]="node.status" severity="secondary" />
                } @else {
                  <span class="lineage-empty">—</span>
                }
              </li>
            }
          </ul>
        </section>

        <ain-growth-journey-monitoring-panel
          [journeyId]="item.id"
          [available]="monitoringAvailable(item)" />

        <ain-growth-journey-improvement-panel
          [journeyId]="item.id"
          [available]="monitoringAvailable(item)" />
      }
    </ain-page>
  `,
  styles: [`
    .toolbar { display: flex; gap: 8px; flex-wrap: wrap; margin-bottom: 12px; }
    .card { border: 1px solid #e5e7eb; border-radius: 12px; background: #fff; padding: 12px; display: grid; gap: 10px; margin-bottom: 12px; }
    .card h3 { margin: 0; font-size: 1rem; }
    .meta-row { display: flex; gap: 8px; flex-wrap: wrap; }
    .meta-card { margin-bottom: 12px; }
    .stepper { list-style: none; margin: 0; padding: 0; display: grid; gap: 8px; }
    .stepper-item { display: flex; gap: 10px; align-items: center; border: 1px solid #e5e7eb; border-radius: 10px; padding: 8px 10px; }
    .stepper-item--current { border-color: #2563eb; background: #eff6ff; }
    .stepper-item--gated { opacity: 0.72; }
    .stepper-index { width: 28px; height: 28px; border-radius: 999px; background: #f3f4f6; display: grid; place-items: center; font-weight: 700; font-size: 0.85rem; flex-shrink: 0; }
    .stepper-item--current .stepper-index { background: #2563eb; color: #fff; }
    .stepper-body { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; min-width: 0; }
    .stepper-label { font-weight: 600; }
    .next-action-card { border-color: #bfdbfe; background: #f8fbff; }
    .next-action-hint { margin: 0; color: #334155; }
    .next-action-muted { margin: 0; color: #6b7280; }
    .lineage-list { list-style: none; margin: 0; padding: 0; display: grid; gap: 8px; }
    .lineage-item { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; border-bottom: 1px solid #f3f4f6; padding-bottom: 8px; }
    .lineage-item:last-child { border-bottom: 0; padding-bottom: 0; }
    .lineage-kind { min-width: 160px; font-weight: 600; }
    .lineage-empty { color: #9ca3af; }
  `]
})
export class GrowthJourneyDashboardComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly journeyService = inject(GrowthJourneyService);
  private readonly workflowApi = inject(WorkflowApiService);
  private readonly i18n = inject(LocalizationService);
  private readonly messages = inject(MessageService);

  readonly loading = signal(true);
  readonly refreshing = signal(false);
  readonly error = signal<string | null>(null);
  readonly journey = signal<GrowthGuidedJourneyDetail | null>(null);
  readonly stepperItems = signal<StepperItem[]>([]);
  readonly workflowTaskId = signal<string | null>(null);
  readonly workflowLookupDone = signal(false);

  ngOnInit(): void {
    this.route.paramMap.subscribe(params => {
      const journeyId = params.get('journeyId');
      if (!journeyId) {
        this.error.set(this.t('common.notFound'));
        this.loading.set(false);
        return;
      }

      this.load(journeyId);
    });
  }

  pageTitle(): string {
    return this.journey()?.title ?? this.t('growth.journey.guided.title');
  }

  statusLabel(status: unknown): string {
    const normalized = normalizeGuidedJourneyStatus(status);
    return this.t(GUIDED_JOURNEY_STATUS_I18N_KEYS[normalized]);
  }

  statusSeverity(status: unknown): 'secondary' | 'info' | 'success' | 'warn' | 'danger' | 'contrast' {
    return resolveGuidedJourneyStatusSeverity(status);
  }

  stepLabelKey(step: unknown): string {
    return growthJourneyStepI18nKey(step);
  }

  stepStateLabelKey(state: unknown): string {
    return growthJourneyStepStateI18nKey(state);
  }

  stepStateSeverity(state: unknown): 'secondary' | 'info' | 'success' | 'warn' | 'danger' | 'contrast' {
    return resolveStepStateSeverity(state);
  }

  lineageKindKey(kind: string): string {
    return growthJourneyLineageKindI18nKey(kind);
  }

  monitoringAvailable(item: GrowthGuidedJourneyDetail): boolean {
    return isJourneyMonitoringAvailable(item.currentStep);
  }

  refresh(): void {
    const current = this.journey();
    if (!current) {
      return;
    }

    this.refreshing.set(true);
    this.journeyService.refreshJourney(current.id).subscribe({
      next: item => {
        this.hydrate(item);
        this.refreshing.set(false);
        this.messages.add({ severity: 'success', summary: this.t('growth.journey.guided.dashboard.refreshed') });
      },
      error: err => {
        this.refreshing.set(false);
        this.messages.add({ severity: 'warn', summary: this.resolveActionError(err) });
      }
    });
  }

  t(key: string): string {
    return this.i18n.translate(key);
  }

  private load(journeyId: string): void {
    this.loading.set(true);
    this.error.set(null);
    this.journeyService.getJourney(journeyId).subscribe({
      next: item => {
        this.hydrate(item);
        this.loading.set(false);
      },
      error: () => {
        this.error.set(this.t('common.notFound'));
        this.loading.set(false);
      }
    });
  }

  private hydrate(item: GrowthGuidedJourneyDetail): void {
    this.journey.set(item);
    this.stepperItems.set(this.buildStepperItems(item));
    this.resolveWorkflowTask(item);
  }

  private resolveWorkflowTask(item: GrowthGuidedJourneyDetail): void {
    this.workflowTaskId.set(null);
    this.workflowLookupDone.set(false);

    if (!item.workflowProcessInstanceId) {
      this.workflowLookupDone.set(true);
      return;
    }

    // Transitions are workflow-driven: route the operator to their open task for this journey's
    // process instance instead of hardcoding the next-step link.
    this.workflowApi.getInbox().subscribe({
      next: tasks => {
        const open = tasks.find(task => task.processInstanceId === item.workflowProcessInstanceId);
        this.workflowTaskId.set(open?.id ?? null);
        this.workflowLookupDone.set(true);
      },
      error: () => {
        this.workflowTaskId.set(null);
        this.workflowLookupDone.set(true);
      }
    });
  }

  private buildStepperItems(item: GrowthGuidedJourneyDetail): StepperItem[] {
    const stateByStep = new Map(item.stepStates.map(entry => [entry.step, entry]));
    const currentStep = normalizeGrowthJourneyStep(item.currentStep);

    return GROWTH_JOURNEY_STEP_ORDER.map(step => {
      const entry = stateByStep.get(step);
      return {
        step,
        state: entry?.state ?? 'Pending',
        isGated: entry?.isGated ?? false,
        isCurrent: step === currentStep
      };
    });
  }

  private resolveActionError(error: unknown): string {
    const code = this.extractErrorCode(error);
    return code ? this.t(code) : this.t('common.error');
  }

  private extractErrorCode(error: unknown): string | null {
    if (!(error instanceof HttpErrorResponse)) {
      return null;
    }

    const body = error.error as Record<string, unknown> | null;
    if (!body) {
      return null;
    }

    if (typeof body['error'] === 'string') {
      return body['error'];
    }

    return null;
  }
}
